import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import collect from 'collect.js';
import { Button, Drawer, Form, Input, Select, Tabs } from 'antd';
import { useSelector } from 'react-redux';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import CurrencyInput from '../../components/form/general/CurrencyInput';
import useApiClient from '../../hooks/api/useApiClient';
import useScreenType from '../../hooks/context/useScreenType';
import useForm from '../../hooks/utility/useForm';
import NutritionInfoFields from '../../components/form/menu/NutritionInfoFields';
import AvailabilityCard from '../../components/menu/AvailabilityCard';
import MenuSubjectEntryScope from '../product/MenuSubjectEntryScope';

export const Tab = {
  General: 'general',
  NutritionInfo: 'nutrition_info',
};

export default function ModifierModal({
  toggleModal,
  selectedModifierGroup,
  selectedModifier,
  companyModifierGroups,
  onSuccess,
  isMaster,
  externalIdVisible,
  externalIdEditable,
  integrationName,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Modifier:ModifierModal' });
  const companyId = useSelector((state) => state.currentCompany.company?.id);
  const { ModifiersManager } = useApiClient();
  const { setFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel();
  const { isDesktop } = useScreenType();

  const [activeTab, setActiveTab] = useState(Tab.General);
  const [loading, setLoading] = useState(false);
  const companies = useSelector(
    (state) => state.currentCompany.company.franchise?.companies ?? state.currentFranchise.franchise?.companies,
  );
  const isAvailable = useMemo(() => {
    if (!selectedModifier) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster ? selectedModifier.is_available : selectedModifier.is_available?.includes(companyId);
  }, [companies, selectedModifier, isMaster, companyId]);
  const isVisible = useMemo(() => {
    if (!selectedModifier) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster ? selectedModifier.is_visible : selectedModifier.is_visible?.includes(companyId);
  }, [companies, selectedModifier, isMaster, companyId]);
  const isAvailableForPickup = useMemo(() => {
    if (!selectedModifier) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster
      ? selectedModifier.is_available_for_pickup
      : selectedModifier.is_available_for_pickup?.includes(companyId);
  }, [companies, selectedModifier, isMaster, companyId]);
  const isAvailableForDelivery = useMemo(() => {
    if (!selectedModifier) {
      return isMaster ? companies.map((c) => c.id) : true;
    }

    return isMaster
      ? selectedModifier.is_available_for_delivery
      : selectedModifier.is_available_for_delivery?.includes(companyId);
  }, [companies, selectedModifier, isMaster, companyId]);
  const { data, update } = useForm({
    modifier_groups: collect(selectedModifier?.modifier_groups ?? [selectedModifierGroup])
      .pluck('id')
      .toArray(),
    identifiers: selectedModifier?.identifiers ?? [],
    name: selectedModifier?.name ?? null,
    external_id: selectedModifier?.external_id ?? null,
    price: selectedModifier?.price ?? 0,
    is_available: isAvailable,
    is_visible: isVisible,
    is_available_for_pickup: isAvailableForPickup,
    is_available_for_delivery: isAvailableForDelivery,
    nutrition_info_by_source: selectedModifier?.nutrition_info_by_source ?? {},
    nutrition_info_breakdown: selectedModifier?.nutrition_info_breakdown ?? {},
    has_nutrition_table: Boolean(selectedModifier?.nutrition_info?.nutrition_per_100),
  });

  function prepareNutritionInfo() {
    if (!Object.keys(data.nutrition_info_breakdown).length) {
      return [];
    }

    return {
      nutrition_info_breakdown: collect(structuredClone(data.nutrition_info_breakdown))
        .when(!data.has_nutrition_table, (collection) => collection.forget('nutrition_per_100'))
        .all(),
    };
  }

  async function save() {
    resetFeedback();
    const manualNutritionInfo = Object.entries(
      data.nutrition_info_by_source?.manual ? structuredClone(data.nutrition_info_by_source?.manual) : {},
    ).filter(([field]) => data.nutrition_info_breakdown?.[field]?.source === 'manual');
    const _data = {
      ...collect(structuredClone(data))
        .forget('nutrition_info_by_source')
        .forget('nutrition_info')
        .forget('nutrition_info_breakdown')
        // Only persist external_id when the user could actually edit it (HubRise
        // connected, sync off, company-level entry) — never clobber it otherwise.
        .when(!externalIdEditable, (collection) => collection.forget('external_id'))
        .when(Boolean(manualNutritionInfo.length), (collection) =>
          collection.put('nutrition_info_by_source', Object.fromEntries(manualNutritionInfo)),
        )
        .all(),
      ...prepareNutritionInfo(),
    };
    setLoading(true);
    const { data: responseData, success } = selectedModifier?.id
      ? await ModifiersManager.update(selectedModifier?.id, _data)
      : await ModifiersManager.create({
          ..._data,
          is_master: isMaster,
        });
    setLoading(false);

    if (success) {
      return successHandler().then(() => toast.success(t('toasts.changes_got_saved')));
    }

    return errorHandler(responseData);
  }

  async function successHandler() {
    toggleModal();
    onSuccess();
  }

  function errorHandler(data) {
    setFeedback({
      message: data?.message,
      type: 'error',
      errors: data?.errors,
    });
  }

  const props = {
    isMaster,
    data,
    update,
    companyModifierGroups,
    externalIdVisible,
    externalIdEditable,
    integrationName,
  };

  return (
    <Drawer
      open
      title={!selectedModifier ? t('title') : selectedModifier.name}
      onClose={() => toggleModal()}
      scrollable
      size={window.innerWidth * (isDesktop ? 0.5 : 1)}
      extra={
        <Button type="primary" onClick={save} loading={loading}>
          {t('buttons.save')}
        </Button>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: Tab.General, label: t(`tabs.${Tab.General}`) },
          { key: Tab.NutritionInfo, label: t(`tabs.${Tab.NutritionInfo}`) },
        ]}
      />
      <Form layout="vertical">
        {renderFeedbackLabel}
        {activeTab === Tab.General && <GeneralTab {...props} />}
        {activeTab === Tab.NutritionInfo && <NutritionInfoFields {...props} />}
      </Form>
    </Drawer>
  );
}

function GeneralTab({
  data,
  update,
  companyModifierGroups,
  isMaster,
  externalIdVisible,
  externalIdEditable,
  integrationName,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Modifier:ModifierModal' });

  const toOption = (object, label = 'name') => ({ value: object.id, label: object[label] });
  const modifierGroupOptions = useMemo(
    () =>
      collect(companyModifierGroups)
        .map((item) => toOption(item))
        .toArray(),
    [companyModifierGroups],
  );

  return (
    <>
      <Form.Item label={t('form.modifier_groups.label')}>
        <Select
          allowClear
          mode="multiple"
          showSearch={{ optionFilterProp: 'label' }}
          options={modifierGroupOptions}
          value={data.modifier_groups}
          onChange={(value) => update('modifier_groups', value)}
        />
      </Form.Item>
      <Form.Item label={t('form.name.label')}>
        <Input
          value={data.name ?? ''}
          onChange={(e) => update('name', e.target.value)}
          placeholder={t('form.name.placeholder')}
        />
      </Form.Item>
      {externalIdVisible && (
        <Form.Item
          label={t('form.external_id.label', { integration: integrationName })}
          help={externalIdEditable ? undefined : t('form.external_id.read_only_hint')}
        >
          <Input
            value={data.external_id ?? ''}
            onChange={(e) => update('external_id', e.target.value)}
            placeholder={t('form.external_id.placeholder')}
            disabled={!externalIdEditable}
          />
        </Form.Item>
      )}
      <Form.Item label={t('form.price.label')}>
        <CurrencyInput value={data.price} onChange={(value) => update('price', value)} />
      </Form.Item>
      <MenuSubjectEntryScope value={data.is_available} update={update} isMaster={isMaster} field="is_available" />
      {isMaster ? (
        <>
          <MenuSubjectEntryScope value={data.is_visible} update={update} isMaster={isMaster} field="is_visible" />
          <MenuSubjectEntryScope
            value={data.is_available_for_pickup}
            update={update}
            isMaster={isMaster}
            field="is_available_for_pickup"
          />
          <MenuSubjectEntryScope
            value={data.is_available_for_delivery}
            update={update}
            isMaster={isMaster}
            field="is_available_for_delivery"
          />
        </>
      ) : (
        <AvailabilityCard data={data} update={update} t={t} />
      )}
    </>
  );
}
